import React from 'react';
import { Link } from 'react-router-dom';

function WhatDeclarationMeansForBuyers() {
  return (
    <>
      <div className="bg-[#F9FAFB] min-h-screen font-sans">
        {/* Header Section */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-6 py-16 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-[#102A43] mb-6">What AASC Accountability Declaration Means for Buyers</h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Declaration is a signal, not a guarantee. Here is exactly how to interpret the AASC mark when evaluating an organization using AI.
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 py-12 space-y-16">
          
          {/* Section 1: The Baseline Signal */}
          <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-10">
            <h2 className="text-2xl font-bold text-[#102A43] mb-6 border-b border-gray-100 pb-4">The Baseline Signal</h2>
            <div className="text-[#64748B] leading-relaxed space-y-4">
              <p>
                In a market flooded with new organizations using AI, it is difficult to distinguish between organizations that have operational maturity and those that are improvising. AASC accountability declaration provides a specific, verifiable signal: <strong>Baseline Controls Exist.</strong>
              </p>
              <p>
                It tells you that the agency has not just built an AI system, but has also built the safety rails around it. It is evidence that they have thought through failure modes before they handle your data.
              </p>
            </div>
          </section>

          {/* Section 2: What Declaration Means */}
          <section>
            <h2 className="text-2xl font-bold text-[#102A43] mb-6">What Declaration Means</h2>
            <div className="bg-green-50 rounded-xl border border-green-100 p-8 md:p-10">
              <p className="text-green-900 font-medium mb-6">When you see the AASC mark, it confirms that the agency has documented evidence of:</p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-200 text-green-800 flex items-center justify-center mr-3 mt-0.5">✓</span>
                  <span className="text-green-900"><strong>A Named Human Owner:</strong> Specific individuals are accountable for AI outcomes. There is no "the algorithm did it" excuse.</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-200 text-green-800 flex items-center justify-center mr-3 mt-0.5">✓</span>
                  <span className="text-green-900"><strong>Defined Boundaries:</strong> The agency has clearly scoped what the AI is allowed to do and, critically, what it is NOT allowed to do.</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-200 text-green-800 flex items-center justify-center mr-3 mt-0.5">✓</span>
                  <span className="text-green-900"><strong>Defined Limits:</strong> There are set thresholds (e.g., confidence scores, spend limits) that govern autonomous behavior.</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-200 text-green-800 flex items-center justify-center mr-3 mt-0.5">✓</span>
                  <span className="text-green-900"><strong>Human Intervention Rules:</strong> There is a documented protocol for when a human must step in and take over.</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-200 text-green-800 flex items-center justify-center mr-3 mt-0.5">✓</span>
                  <span className="text-green-900"><strong>Evidence Retention:</strong> The agency keeps logs of interventions and errors to prove they are watching the system.</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 3: What Declaration Does Not Mean */}
          <section>
            <h2 className="text-2xl font-bold text-[#102A43] mb-6">What Declaration Does <span className="text-red-600">NOT</span> Mean</h2>
            <div className="bg-white rounded-xl border border-gray-200 p-8 md:p-10 shadow-sm">
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 text-red-800 flex items-center justify-center mr-3 mt-0.5">✕</span>
                  <span className="text-[#64748B]"><strong>Guaranteed Results:</strong> It does not ensure your project will be profitable or successful.</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 text-red-800 flex items-center justify-center mr-3 mt-0.5">✕</span>
                  <span className="text-[#64748B]"><strong>Perfect Systems:</strong> It does not mean the AI will never make a mistake. It means there is a process for handling mistakes.</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 text-red-800 flex items-center justify-center mr-3 mt-0.5">✕</span>
                  <span className="text-[#64748B]"><strong>Zero Risk:</strong> All technology carries risk. Declaration demonstrates risk <em>management</em>, not risk elimination.</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 text-red-800 flex items-center justify-center mr-3 mt-0.5">✕</span>
                  <span className="text-[#64748B]"><strong>Legal Approval:</strong> It is not a substitute for legal advice or regulatory requirements (e.g., GDPR, EU AI Act).</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 text-red-800 flex items-center justify-center mr-3 mt-0.5">✕</span>
                  <span className="text-[#64748B]"><strong>Business Endorsement:</strong> The Council does not evaluate the creative quality or business acumen of the agency.</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 4: Why This Matters */}
          <section className="bg-blue-50 rounded-xl p-8 md:p-10 border border-blue-100">
            <h2 className="text-2xl font-bold text-[#2F80ED] mb-4">Why This Matters</h2>
            <p className="text-blue-800 leading-relaxed text-lg">
              The difference is between handling problems <em>after</em> they cause damage versus preparing for them <em>before</em> deployment.
            </p>
            <p className="text-blue-800 leading-relaxed mt-4">
              Agencies without these controls are often learning on your dime, improvising responses when things go wrong. Declared agencies have pre-validated protocols. They treat AI as a professional industrial process, not a magic trick.
            </p>
          </section>

          {/* Section 5: How Buyers Should Use It */}
          <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-10">
            <h2 className="text-2xl font-bold text-[#102A43] mb-6 border-b border-gray-100 pb-4">How Buyers Should Use It</h2>
            <div className="text-[#64748B] leading-relaxed space-y-4">
              <p>
                Use declaration as a <strong>starting filter</strong>.
              </p>
              <p>
                When you receive proposals, check for the AASC mark. If an agency has not made a declaration, ask them: <em>"How do you document human oversight and accountability for your AI agents?"</em>
              </p>
              <p>
                Their answer—or lack thereof—will tell you a lot about their operational maturity. Declaration simply does this due diligence for you in advance.
              </p>
            </div>
          </section>

          {/* Section 6: The Bottom Line */}
          <section className="text-center py-8">
            <h2 className="text-3xl font-bold text-[#102A43] mb-4">The Bottom Line</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Declaration takes the "black box" out of AI operations. It ensures that when you hire an agency, you are hiring a team that remains accountable for the technology they deploy.
            </p>
          </section>

          {/* Footer & CTA */}
          <div className="border-t border-gray-200 pt-12 mt-4 text-center">
            <p className="text-gray-600 mb-6">
              Verify declaration status at: <a href="/registry" className="text-blue-600 hover:underline font-medium">/registry</a>
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
               <Link to="/standards" className="px-6 py-3 bg-[#2F80ED] text-white font-semibold rounded-md hover:bg-[#2563eb] transition-colors">
                 View Standards
               </Link>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}

export default WhatDeclarationMeansForBuyers;
